import { p as proxyCustomElement, H, c as createEvent, h } from './p-BJEvNVLM.js';
import { b as bem, g as global$1, i as isNodeEnvironment } from './p-g1JgJ7wn.js';
import { t as tippy } from './p-365bzS87.js';
import { u as useKeyboard } from './p-C8iutPqo.js';
import { t as transformTagName, r as resolveAppearance } from './p-CkrRd9Ud.js';

const cosmosActionMenuCss = ":host{display:inline-block;height:0;width:0}.cosmos-action-menu{pointer-events:none;position:relative;visibility:hidden;z-index:1}.cosmos-action-menu--open{pointer-events:auto;visibility:visible}.cosmos-action-menu__menu-container{background-color:var(--cosmos-action-menu-background-color, #FFFFFF);border:1px solid rgba(0, 15, 30, 0.2);border-color:var(--cosmos-action-menu-border-color, rgba(0, 15, 30, 0.2));border-radius:var(--cosmos-action-menu-border-radius, 4px);box-shadow:var(--cosmos-action-menu-dropshadow, 0px 16px 24px 0px rgba(0, 15, 30, 0.0588235294), 0px 4px 8px 0px rgba(0, 15, 30, 0.0196078431));box-sizing:border-box;max-height:var(--cosmos-action-menu-max-height);max-width:calc(100vw - 16px);min-width:148px;overflow:auto;position:absolute;right:0;top:100%;-webkit-user-select:none;user-select:none;width:var(--cosmos-action-menu-width)}.cosmos-action-menu__menu-container ::slotted([data-component-name=cosmos-action-menu-group]:last-of-type){--cosmos-action-menu-group-padding-bottom:4px}.cosmos-action-menu__menu-container--appearance-light ::slotted([data-component-name=cosmos-action-menu-group]:nth-of-type(n+2)){border-top:1px solid var(--cosmos-action-menu-divider-color, rgba(0, 15, 30, 0.1));padding-top:4px}.cosmos-action-menu__menu-container--appearance-dark{background-color:var(--cosmos-action-menu-background-color, #00162B);border:1px solid rgba(255, 255, 255, 0.45);border-color:var(--cosmos-action-menu-border-color, rgba(255, 255, 255, 0.45))}.cosmos-action-menu__menu-container--appearance-dark ::slotted([data-component-name=cosmos-action-menu-group]:nth-of-type(n+2)){border-top:1px solid var(--cosmos-action-menu-divider-color, rgba(255, 255, 255, 0.2))}.cosmos-action-menu__menu-container--appearance-dark .cosmos-action-menu__header ::slotted([slot=header]){background-color:var(--cosmos-action-menu-header-background-color, #00162B)}.cosmos-action-menu__menu{display:flex;flex-direction:column;left:0;overflow:auto;padding:var(--cosmos-action-menu-padding, 8px)}.cosmos-action-menu__menu ::slotted([data-component-name=cosmos-action-menu-item]){margin-bottom:2px}.cosmos-action-menu__menu ::slotted([data-component-name=cosmos-action-menu-item]:last-child){margin-bottom:0}.cosmos-action-menu__header{display:flex}.cosmos-action-menu__header ::slotted([slot=header]){background-color:var(--cosmos-action-menu-header-background-color, #F8F8F8);padding:12px 16px;width:100%}";

const CosmosActionMenu$1 = /*@__PURE__*/ proxyCustomElement(class CosmosActionMenu extends H {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.hide = createEvent(this, "hide", 7);
        this.show = createEvent(this, "show", 7);
        /**
         * Horizontal position of the Action Menu relative to its anchor element.
         */
        this.align = 'start';
        /**
         * Toggles the visibility of the Action Menu.
         */
        this.open = false;
        /**
         * Defines whether the Action Menu should flip to the top with regard to its anchor element.
         * By default, the vertical position of the Action Menu is below its trigger but is automatically adjusted if it would otherwise be cut off by the edge of the screen.
         */
        this.placement = 'below';
        /**
         * Determines how the menu is triggered to open:
         * - 'hover': Opens on trigger element mouse hover. Also supports tap on mobile devices.
         * - 'default': Opens on trigger element click (standard behavior)
         */
        this.triggerType = 'default';
        this.computedAppearance = 'light';
        this.isMenuVisible = this.open;
        this.generateClassNames = bem('cosmos-action-menu');
        this.actionItems = [];
        this.actionMenuItemTag = transformTagName('cosmos-action-menu-item');
        this.PLACEMENTS = {
            above: {
                start: ['top-start', 'top', 'top-end'],
                center: ['top', 'top-end', 'bottom', 'bottom-end'],
                end: ['top-end', 'top', 'top-start'],
            },
            below: {
                start: ['bottom-start', 'bottom', 'bottom-end'],
                center: ['bottom', 'bottom-end', 'top', 'top-end'],
                end: ['bottom-end', 'bottom', 'bottom-start'],
            },
        };
        this.FALLBACK_PLACEMENTS = {
            above: {
                start: ['top', 'bottom-start', 'bottom', 'bottom-end'],
                center: ['top-start', 'top-end', 'bottom', 'bottom-end'],
                end: ['top', 'top-start', 'bottom-end', 'bottom', 'bottom-start'],
            },
            below: {
                start: ['bottom', 'bottom-start', 'top-start', 'top'],
                center: ['bottom-start', 'bottom-end', 'top', 'top-end'],
                end: ['bottom', 'bottom-start', 'top-end', 'top', 'top-start'],
            },
        };
        this.handleOutsideClick = (event) => {
            if (!this.isMenuVisible) {
                return;
            }
            if (this.container && !this.container.contains(event.target)) {
                this.closeMenu();
            }
        };
        this.closeMenu = () => {
            var _a;
            this.isMenuVisible = false;
            (_a = this.tippyInstance) === null || _a === void 0 ? void 0 : _a.hide();
        };
        this.handleMenuClick = (event) => {
            // if target is a menu item, allow the click event to propagate
            if (event.target.closest('cosmos-action-menu-item')) {
                return;
            }
            else {
                event.stopPropagation();
            }
        };
        this.handleSlotChange = () => {
            this.initActions();
            if (this.cleanupKeyboard) {
                this.cleanupKeyboard();
                this.initKeyboard();
            }
        };
    }
    handleAppearanceChange(appearance) {
        if (appearance === this.computedAppearance) {
            return;
        }
        this.computedAppearance = appearance;
    }
    handleOpenChange(show) {
        var _a, _b;
        if (show) {
            (_a = this.tippyInstance) === null || _a === void 0 ? void 0 : _a.show();
            return;
        }
        (_b = this.tippyInstance) === null || _b === void 0 ? void 0 : _b.hide();
    }
    handleTriggerTypeChange(triggerType) {
        var _a, _b;
        if (triggerType === 'hover') {
            (_a = this.tippyInstance) === null || _a === void 0 ? void 0 : _a.setProps({
                trigger: 'mouseenter',
                delay: [100, 200],
            });
        }
        else {
            (_b = this.tippyInstance) === null || _b === void 0 ? void 0 : _b.setProps({
                trigger: 'manual',
                delay: [0, 0],
            });
        }
    }
    getAnchorElement() {
        return global$1.document.getElementById(this.anchor);
    }
    getTriggerElement() {
        return global$1.document.getElementById(this.trigger);
    }
    initActions() {
        const actionItems = Array.from(this.element.querySelectorAll(this.actionMenuItemTag));
        this.actionItems = actionItems.map((item) => {
            return {
                index: actionItems.indexOf(item),
                ref: item,
            };
        });
    }
    useAnchorOrTrigger() {
        const anchorElement = this.getAnchorElement();
        const triggerElement = this.getTriggerElement();
        // if both a trigger and an anchor are provided, use the anchor
        if (anchorElement && triggerElement) {
            return anchorElement;
        }
        else if (anchorElement && !triggerElement) {
            // if only an anchor is provided, use the anchor
            return anchorElement;
        }
        else if (!anchorElement && triggerElement) {
            // if only a trigger is provided, use the trigger
            // this is the default behavior
            return triggerElement;
        }
        return null;
    }
    computePlacement() {
        const placement = this.PLACEMENTS[this.placement][this.align][0];
        const fallBackPlacement = this.FALLBACK_PLACEMENTS[this.placement][this.align];
        return {
            placement,
            fallBackPlacement,
        };
    }
    computeTriggerType() {
        const triggerType = this.triggerType;
        if (triggerType === 'hover') {
            return 'mouseenter';
        }
        else {
            return 'manual';
        }
    }
    initTippy() {
        if (isNodeEnvironment()) {
            return;
        }
        const menuContainer = this.menuContainer;
        const tippyElement = this.useAnchorOrTrigger();
        if (!tippyElement || !menuContainer) {
            return;
        }
        const { placement, fallBackPlacement } = this.computePlacement();
        this.tippyInstance = tippy(tippyElement, {
            appendTo: this.container,
            trigger: this.computeTriggerType(),
            offset: [0, 8],
            placement,
            interactive: true,
            hideOnClick: false,
            allowHTML: true,
            delay: this.triggerType === 'hover' ? [100, 200] : [0, 0],
            aria: {
                content: null,
                expanded: false,
            },
            popperOptions: {
                modifiers: [
                    {
                        name: 'flip',
                        options: {
                            fallbackPlacements: fallBackPlacement,
                            boundary: 'viewport',
                        },
                    },
                    {
                        name: 'preventOverflow',
                        options: {
                            boundary: 'viewport',
                            padding: 8,
                            altAxis: true,
                            tether: false,
                        },
                    },
                ],
            },
            onCreate() {
                tippyElement.setAttribute('aria-haspopup', 'true');
            },
            onShow: () => {
                this.show.emit();
                this.isMenuVisible = true;
                tippyElement.setAttribute('aria-expanded', 'true');
            },
            onHide: () => {
                this.hide.emit();
                this.isMenuVisible = false;
                tippyElement.setAttribute('aria-expanded', 'false');
            },
            render() {
                return {
                    popper: menuContainer,
                };
            },
        });
        if (this.open) {
            this.tippyInstance.show();
        }
    }
    initKeyboard() {
        const trigger = this.getTriggerElement();
        const menu = this.menuContainer;
        const tippyInstance = this.tippyInstance;
        const actionItems = this.actionItems;
        if (!trigger || !menu || !tippyInstance || actionItems.length === 0) {
            return;
        }
        const keyboard = useKeyboard({ trigger, menu, tippyInstance }, this.actionItems);
        this.cleanupKeyboard = keyboard.cleanup;
        keyboard.setup();
    }
    setTriggerAccessibilityAttributes() {
        const trigger = this.getTriggerElement();
        if (!trigger) {
            return;
        }
        trigger.setAttribute('aria-haspopup', 'true');
        trigger.setAttribute('aria-expanded', this.isMenuVisible ? 'true' : 'false');
        trigger.setAttribute('aria-controls', 'cosmos-action-menu');
    }
    componentDidLoad() {
        resolveAppearance({
            instance: this,
            modeToAppearanceMap: { dark: 'dark', light: 'light' },
            callback: this.handleAppearanceChange.bind(this),
        });
        this.initActions();
        this.initTippy();
        this.initKeyboard();
        this.setTriggerAccessibilityAttributes();
        global$1.window.addEventListener('click', this.handleOutsideClick);
    }
    disconnectedCallback() {
        var _a;
        global$1.window.removeEventListener('click', this.handleOutsideClick);
        if (this.cleanupKeyboard) {
            this.cleanupKeyboard();
        }
        (_a = this.tippyInstance) === null || _a === void 0 ? void 0 : _a.destroy();
    }
    render() {
        return (h("div", { key: '7ee4d231166a705f10bd3553de056d5163357de7', class: this.generateClassNames({
                appearance: this.computedAppearance,
                open: this.isMenuVisible,
            }), id: "cosmos-action-menu", ref: (el) => (this.container = el), onClick: this.handleMenuClick, role: "menu", tabIndex: -1, "aria-labelledby": this.trigger, part: "base" }, h("div", { key: '678bfb8c6f95d7072537ba2ac20a8befd1744ec7', class: this.generateClassNames('menu-container', {
                appearance: this.computedAppearance,
            }), ref: (el) => (this.menuContainer = el) }, h("div", { key: '0386289c78232e1cd08af0150a1d6c8aca89a0c5', class: this.generateClassNames('header') }, h("slot", { key: '4494ff54276b9d05201f68ec7d9bca608bfc7b45', name: "header" })), h("div", { key: '5c83901cf8f46fb58f3160230d050a0e4d51061a', class: this.generateClassNames('menu'), tabIndex: -1, part: "menu" }, h("slot", { key: '92246b82b2020fe09e7e7483bda24c1245940163', onSlotchange: this.handleSlotChange })))));
    }
    get element() { return this; }
    static get watchers() { return {
        "appearance": ["handleAppearanceChange"],
        "open": ["handleOpenChange"],
        "triggerType": ["handleTriggerTypeChange"]
    }; }
    static get style() { return cosmosActionMenuCss; }
}, [1, "cosmos-action-menu", {
        "align": [513],
        "anchor": [513],
        "appearance": [513],
        "open": [516],
        "placement": [513],
        "trigger": [513],
        "triggerType": [513, "trigger-type"],
        "computedAppearance": [32],
        "isMenuVisible": [32]
    }, undefined, {
        "appearance": ["handleAppearanceChange"],
        "open": ["handleOpenChange"],
        "triggerType": ["handleTriggerTypeChange"]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["cosmos-action-menu"];
    components.forEach(tagName => { switch (tagName) {
        case "cosmos-action-menu":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, CosmosActionMenu$1);
            }
            break;
    } });
}

const CosmosActionMenu = CosmosActionMenu$1;
const defineCustomElement = defineCustomElement$1;

export { CosmosActionMenu, defineCustomElement };
